<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import MediaLibraryPreview from './MediaLibraryPreview.vue';
const props = defineProps({
  product: { type: Object, required: true },
  canEdit: Boolean,
});
const emit = defineEmits(['open', 'add', 'edit']);
const { t } = useI18n();
const index = ref(0);
const preview = computed(
  () => props.product.previews[index.value] || props.product.previews[0]
);
</script>

<template>
  <article
    class="flex min-w-0 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
  >
    <div>
      <h2 class="m-0 break-words text-base font-semibold text-n-slate-12">
        {{ product.name }}
      </h2>
      <p class="mb-0 mt-1 text-xs text-n-slate-11">
        {{ product.category_name || t('MEDIA_LIBRARY.NO_CATEGORY') }}
      </p>
    </div>
    <button
      type="button"
      class="relative block h-48 w-full rounded-lg border border-n-weak bg-n-field focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
      :aria-label="t('MEDIA_LIBRARY.OPEN_PRODUCT') + ': ' + product.name"
      @click="emit('open')"
    >
      <MediaLibraryPreview
        v-if="preview"
        :key="preview.id"
        :item="preview"
        class="h-full w-full !bg-n-field"
      />
      <span
        v-else
        class="i-lucide-image size-8 text-n-slate-10"
        aria-hidden="true"
      />
      <span
        class="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-n-solid-2 px-2 py-1 text-xs text-n-slate-11"
        ><span class="i-lucide-images size-3.5" />{{
          t('MEDIA_LIBRARY.PHOTO_COUNT', { count: product.photo_count })
        }}
      </span>
    </button>
    <div v-if="product.previews.length > 1" class="grid grid-cols-3 gap-2">
      <button
        v-for="(item, position) in product.previews"
        :key="item.id"
        type="button"
        :aria-label="item.title"
        :aria-pressed="index === position"
        class="h-16 min-w-0 overflow-hidden rounded-lg border bg-n-field focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
        :class="index === position ? 'border-n-blue-9' : 'border-n-weak'"
        @click="index = position"
      >
        <MediaLibraryPreview :item="item" class="h-full w-full !bg-n-field" />
      </button>
    </div>
    <div v-if="product.tags.length" class="flex flex-wrap gap-1.5">
      <span
        v-for="tag in product.tags"
        :key="tag"
        class="max-w-full break-words rounded-md bg-n-blue-2 px-2 py-1 text-xs text-n-blue-11"
      >
        {{ tag }}
      </span>
    </div>
    <p class="m-0 flex items-center gap-2 text-xs text-n-slate-11">
      <span class="i-lucide-files size-4 shrink-0" />{{
        t('MEDIA_LIBRARY.MEDIA_COUNT', { count: product.media_count })
      }}
    </p>
    <p
      v-if="product.checklist.required.length"
      class="m-0 flex items-center gap-2 text-xs text-n-slate-11"
    >
      <span class="i-lucide-list-checks size-4 shrink-0" />{{
        t('MEDIA_LIBRARY.EXPECTED_PHOTOS', {
          covered: product.checklist.covered.length,
          total: product.checklist.required.length,
        })
      }}
    </p>
    <footer class="mt-auto flex gap-2 pt-1">
      <Button
        class="min-w-0 flex-1"
        variant="outline"
        :label="t('MEDIA_LIBRARY.OPEN_PRODUCT')"
        icon="i-lucide-chevron-right"
        trailing-icon
        @click="emit('open')"
      />
      <Button
        v-if="canEdit"
        variant="outline"
        color="slate"
        icon="i-lucide-plus"
        :aria-label="t('MEDIA_LIBRARY.ADD')"
        :title="t('MEDIA_LIBRARY.ADD')"
        @click="emit('add')"
      />
      <Button
        v-if="canEdit"
        variant="outline"
        color="slate"
        icon="i-lucide-pencil"
        :aria-label="t('MEDIA_LIBRARY.EDIT_PRODUCT')"
        :title="t('MEDIA_LIBRARY.EDIT_PRODUCT')"
        @click="emit('edit')"
      />
    </footer>
  </article>
</template>
